import {
  ACCOUNT_TYPE_LABELS,
  type AccountType,
  APPLIES_TO_LABELS,
  type AppliesTo,
  amountText,
  COUNTERPARTY_KIND_LABELS,
  type CounterpartyKind,
  describeError,
  FINANCE_ERRORS,
  parseBalance,
} from '@horof/shared';
import { App, Input, Modal, Radio } from 'antd';
import { useState } from 'react';
import { useMutation } from 'urql';
import {
  type Account,
  CreateAccountDocument,
  CreateCategoryDocument,
  CreateCounterpartyDocument,
  CreateProjectDocument,
  financeContext,
  UpdateAccountDocument,
  UpdateCategoryDocument,
  UpdateCounterpartyDocument,
  UpdateProjectDocument,
} from './data';

/**
 * Add or edit the lists the operations use. Every dialog shows what will be
 * saved and saves only on «حفظ»; `onClose` gets the new id after a creation
 * (a picker selects it).
 */

type Done = (createdId: string | null) => void;

export function Field({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor?: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="desk-dialog-field">
      <label htmlFor={htmlFor}>{label}</label>
      {children}
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

function useSave() {
  const { message } = App.useApp();
  const [problem, setProblem] = useState<string | null>(null);
  const run = async (
    action: () => Promise<{ error?: Parameters<typeof describeError>[0] }>,
    done: string,
  ) => {
    setProblem(null);
    const result = await action();
    if (result.error) {
      setProblem(describeError(result.error, 'تعذّر الحفظ.', FINANCE_ERRORS));
      return false;
    }
    message.success(done);
    return true;
  };
  return [problem, setProblem, run] as const;
}

export type CategoryEdit = {
  id: string | null;
  name: string;
  appliesTo: AppliesTo;
};

export function CategoryDialog({
  category,
  onClose,
}: {
  category: CategoryEdit;
  onClose: Done;
}) {
  const [values, setValues] = useState(category);
  const [problem, setProblem, run] = useSave();
  const [{ fetching: a }, create] = useMutation(CreateCategoryDocument);
  const [{ fetching: b }, update] = useMutation(UpdateCategoryDocument);

  async function save() {
    const name = values.name.trim();
    if (!name) return setProblem('اكتب اسم الصنف.');
    const fields = { name, appliesTo: values.appliesTo };
    if (category.id) {
      const id = category.id;
      if (
        await run(
          () => update({ id, patch: fields }, financeContext),
          'حُفظ الصنف',
        )
      ) {
        onClose(null);
      }
      return;
    }
    let created: string | null = null;
    const ok = await run(async () => {
      const r = await create({ category: fields }, financeContext);
      created = r.data?.createOperationCategory?.operationCategory?.id ?? null;
      return r;
    }, `أُضيف الصنف «${name}»`);
    if (ok) onClose(created);
  }

  return (
    <Modal
      open
      title={category.id ? 'تعديل الصنف' : 'صنف جديد'}
      okText="حفظ"
      cancelText="إلغاء"
      confirmLoading={a || b}
      onOk={save}
      onCancel={() => onClose(null)}
    >
      <div className="desk-dialog">
        <Field label="الاسم" htmlFor="category-name">
          <Input
            id="category-name"
            autoFocus
            value={values.name}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
            onPressEnter={save}
          />
        </Field>
        <Field label="يُستعمل في">
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            value={values.appliesTo}
            onChange={(e) =>
              setValues({ ...values, appliesTo: e.target.value })
            }
            options={(['credit', 'debit', 'both'] as const).map((v) => ({
              value: v,
              label: APPLIES_TO_LABELS[v],
            }))}
          />
        </Field>
        {problem && <div className="field-error">{problem}</div>}
      </div>
    </Modal>
  );
}

export type ProjectEdit = {
  id: string | null;
  name: string;
  description: string;
};

export function ProjectDialog({
  project,
  onClose,
}: {
  project: ProjectEdit;
  onClose: Done;
}) {
  const [values, setValues] = useState(project);
  const [problem, setProblem, run] = useSave();
  const [{ fetching: a }, create] = useMutation(CreateProjectDocument);
  const [{ fetching: b }, update] = useMutation(UpdateProjectDocument);

  async function save() {
    const name = values.name.trim();
    if (!name) return setProblem('اكتب اسم المشروع.');
    const fields = { name, description: values.description.trim() || null };
    if (project.id) {
      const id = project.id;
      if (
        await run(
          () => update({ id, patch: fields }, financeContext),
          'حُفظ المشروع',
        )
      ) {
        onClose(null);
      }
      return;
    }
    let created: string | null = null;
    const ok = await run(async () => {
      const r = await create({ project: fields }, financeContext);
      created = r.data?.createProject?.project?.id ?? null;
      return r;
    }, `أُضيف المشروع «${name}»`);
    if (ok) onClose(created);
  }

  return (
    <Modal
      open
      title={project.id ? 'تعديل المشروع' : 'مشروع جديد'}
      okText="حفظ"
      cancelText="إلغاء"
      confirmLoading={a || b}
      onOk={save}
      onCancel={() => onClose(null)}
    >
      <div className="desk-dialog">
        <Field label="الاسم" htmlFor="project-name">
          <Input
            id="project-name"
            autoFocus
            value={values.name}
            onChange={(e) => setValues({ ...values, name: e.target.value })}
          />
        </Field>
        <Field label="وصف (اختياري)" htmlFor="project-description">
          <Input.TextArea
            id="project-description"
            autoSize={{ minRows: 2, maxRows: 4 }}
            value={values.description}
            onChange={(e) =>
              setValues({ ...values, description: e.target.value })
            }
          />
        </Field>
        {problem && <div className="field-error">{problem}</div>}
      </div>
    </Modal>
  );
}

export type CounterpartyEdit = {
  id: string | null;
  name: string;
  kind: CounterpartyKind;
  phone: string;
  note: string;
};

const KINDS = Object.keys(COUNTERPARTY_KIND_LABELS) as CounterpartyKind[];

export function CounterpartyDialog({
  counterparty,
  onClose,
}: {
  counterparty: CounterpartyEdit;
  onClose: Done;
}) {
  const [values, setValues] = useState(counterparty);
  const [problem, setProblem, run] = useSave();
  const [{ fetching: a }, create] = useMutation(CreateCounterpartyDocument);
  const [{ fetching: b }, update] = useMutation(UpdateCounterpartyDocument);
  const set = (patch: Partial<CounterpartyEdit>) =>
    setValues((v) => ({ ...v, ...patch }));

  async function save() {
    const name = values.name.trim();
    if (!name) return setProblem('اكتب اسم الجهة.');
    const fields = {
      name,
      kind: values.kind,
      phone: values.phone.trim() || null,
      note: values.note.trim() || null,
    };
    if (counterparty.id) {
      const id = counterparty.id;
      if (
        await run(
          () => update({ id, patch: fields }, financeContext),
          'حُفظت الجهة',
        )
      ) {
        onClose(null);
      }
      return;
    }
    let created: string | null = null;
    const ok = await run(async () => {
      const r = await create({ counterparty: fields }, financeContext);
      created = r.data?.createCounterparty?.counterparty?.id ?? null;
      return r;
    }, `أُضيفت الجهة «${name}»`);
    if (ok) onClose(created);
  }

  return (
    <Modal
      open
      title={counterparty.id ? 'تعديل الجهة' : 'جهة جديدة'}
      okText="حفظ"
      cancelText="إلغاء"
      confirmLoading={a || b}
      onOk={save}
      onCancel={() => onClose(null)}
    >
      <div className="desk-dialog">
        <Field label="الاسم" htmlFor="cp-name">
          <Input
            id="cp-name"
            autoFocus
            value={values.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </Field>
        <Field label="النوع">
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            value={values.kind}
            onChange={(e) => set({ kind: e.target.value })}
            options={KINDS.map((k) => ({
              value: k,
              label: COUNTERPARTY_KIND_LABELS[k],
            }))}
          />
        </Field>
        <div className="desk-dialog-row">
          <Field label="الهاتف (اختياري)" htmlFor="cp-phone">
            <Input
              id="cp-phone"
              className="ltr-num"
              value={values.phone}
              onChange={(e) => set({ phone: e.target.value })}
            />
          </Field>
          <Field label="ملاحظة (اختيارية)" htmlFor="cp-note">
            <Input
              id="cp-note"
              value={values.note}
              onChange={(e) => set({ note: e.target.value })}
            />
          </Field>
        </div>
        {problem && <div className="field-error">{problem}</div>}
      </div>
    </Modal>
  );
}

export type AccountEdit = {
  id: string | null;
  name: string;
  type: AccountType;
  accountNumber: string;
  openingBalance: string;
  openingDate: string;
};

export const accountEdit = (a: Account): AccountEdit => ({
  id: a.id,
  name: a.name,
  type: a.type,
  accountNumber: a.accountNumber ?? '',
  openingBalance: amountText(a.openingBalance),
  openingDate: a.openingDate ?? '',
});

export function AccountDialog({
  account,
  onClose,
}: {
  account: AccountEdit;
  onClose: Done;
}) {
  const [values, setValues] = useState(account);
  const [problem, setProblem, run] = useSave();
  const [{ fetching: a }, create] = useMutation(CreateAccountDocument);
  const [{ fetching: b }, update] = useMutation(UpdateAccountDocument);
  const set = (patch: Partial<AccountEdit>) =>
    setValues((v) => ({ ...v, ...patch }));
  const openingBalance = parseBalance(values.openingBalance);

  async function save() {
    const name = values.name.trim();
    if (!name) return setProblem('اكتب اسم الحساب.');
    if (openingBalance === null) {
      return setProblem('اكتب رصيد الافتتاح بالأرقام، مثلاً 3873 أو ‎-500.');
    }
    const fields = {
      name,
      type: values.type,
      accountNumber: values.accountNumber.trim() || null,
      openingBalance,
      openingDate: values.openingDate || null,
    };
    if (account.id) {
      const id = account.id;
      if (
        await run(
          () => update({ id, patch: fields }, financeContext),
          'حُفظ الحساب',
        )
      ) {
        onClose(null);
      }
      return;
    }
    let created: string | null = null;
    const ok = await run(async () => {
      const r = await create({ account: fields }, financeContext);
      created = r.data?.createFinancialAccount?.financialAccount?.id ?? null;
      return r;
    }, `أُضيف الحساب «${name}»`);
    if (ok) onClose(created);
  }

  return (
    <Modal
      open
      title={account.id ? 'تعديل الحساب' : 'حساب جديد'}
      okText="حفظ"
      cancelText="إلغاء"
      confirmLoading={a || b}
      onOk={save}
      onCancel={() => onClose(null)}
    >
      <div className="desk-dialog">
        <Field label="الاسم" htmlFor="account-name">
          <Input
            id="account-name"
            autoFocus
            value={values.name}
            onChange={(e) => set({ name: e.target.value })}
          />
        </Field>
        <Field label="النوع">
          <Radio.Group
            optionType="button"
            buttonStyle="solid"
            value={values.type}
            onChange={(e) => set({ type: e.target.value })}
            options={(['cash', 'bank_account'] as const).map((t) => ({
              value: t,
              label: ACCOUNT_TYPE_LABELS[t],
            }))}
          />
        </Field>
        {values.type === 'bank_account' && (
          <Field label="رقم الحساب (اختياري)" htmlFor="account-number">
            <Input
              id="account-number"
              className="ltr-num"
              value={values.accountNumber}
              onChange={(e) => set({ accountNumber: e.target.value })}
            />
          </Field>
        )}
        <div className="desk-dialog-row">
          <Field label="رصيد الافتتاح (درهم)" htmlFor="opening-balance">
            <Input
              id="opening-balance"
              className="ltr-num"
              placeholder="0"
              status={openingBalance === null ? 'error' : undefined}
              value={values.openingBalance}
              onChange={(e) => set({ openingBalance: e.target.value })}
            />
          </Field>
          <Field label="تاريخ الافتتاح" htmlFor="opening-date">
            <Input
              id="opening-date"
              type="date"
              className="ltr-num"
              value={values.openingDate}
              onChange={(e) => set({ openingDate: e.target.value })}
            />
          </Field>
        </div>
        <span className="field-hint">
          الرصيد في تاريخ الافتتاح؛ العمليات اللاحقة تضاف إليه
        </span>
        {problem && <div className="field-error">{problem}</div>}
      </div>
    </Modal>
  );
}
